/**
 * Session-scoped auth storage. Deliberately sessionStorage, not
 * localStorage or a cookie: closing the tab ends the owner's sign-in, and
 * nothing here survives a device restart.
 */

const KEY_TOKENS = "ledger.auth.tokens";
const KEY_VERIFIER = "ledger.auth.verifier";
const KEY_STATE = "ledger.auth.state";
const KEY_RETURN_TO = "ledger.auth.returnTo";
const KEY_FLASH = "ledger.auth.flash";

export interface StoredTokens {
  accessToken: string;
  idToken: string;
  expiresAt: number;
  email: string | null;
}

/** A token is valid until its recorded expiry; there is no refresh token
 * because the app only requests the openid/email scopes. */
export function isTokenValid(tokens: StoredTokens | null, now: number = Date.now()): tokens is StoredTokens {
  return tokens !== null && tokens.expiresAt > now;
}

export function readTokens(): StoredTokens | null {
  const raw = sessionStorage.getItem(KEY_TOKENS);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredTokens;
  } catch {
    sessionStorage.removeItem(KEY_TOKENS);
    return null;
  }
}

export function writeTokens(tokens: StoredTokens): void {
  sessionStorage.setItem(KEY_TOKENS, JSON.stringify(tokens));
}

export function clearTokens(): void {
  sessionStorage.removeItem(KEY_TOKENS);
}

export function writePendingAuthorization(verifier: string, state: string, returnTo: string): void {
  sessionStorage.setItem(KEY_VERIFIER, verifier);
  sessionStorage.setItem(KEY_STATE, state);
  sessionStorage.setItem(KEY_RETURN_TO, returnTo);
}

export function readPendingAuthorization(): { verifier: string; state: string; returnTo: string } | null {
  const verifier = sessionStorage.getItem(KEY_VERIFIER);
  const state = sessionStorage.getItem(KEY_STATE);
  const returnTo = sessionStorage.getItem(KEY_RETURN_TO);
  if (!verifier || !state || !returnTo) return null;
  return { verifier, state, returnTo };
}

export function clearPendingAuthorization(): void {
  sessionStorage.removeItem(KEY_VERIFIER);
  sessionStorage.removeItem(KEY_STATE);
  sessionStorage.removeItem(KEY_RETURN_TO);
}

export function writeFlash(message: string): void {
  sessionStorage.setItem(KEY_FLASH, message);
}

export function readAndClearFlash(): string | null {
  const message = sessionStorage.getItem(KEY_FLASH);
  if (message) sessionStorage.removeItem(KEY_FLASH);
  return message;
}
